import { Component } from 'react'
import type { ReactNode, ErrorInfo } from 'react'
import { AlertCircle } from 'lucide-react'
import * as Sentry from '@sentry/react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ChatErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ChatErrorBoundary caught:', error, info.componentStack)
    Sentry.captureException(error, {
      extra: { componentStack: info.componentStack },
      tags: { feature: 'testops-hub', component: 'chat' },
    })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-12 px-6 text-center">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <p className="text-sm font-medium text-foreground">
            Something went wrong rendering the chat.
          </p>
          <p className="text-xs text-muted-foreground max-w-md">
            {this.state.error?.message || 'An unexpected error occurred.'}
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="mt-2 px-3 py-1.5 text-xs font-medium text-white bg-accent rounded-lg hover:bg-accent/90 transition"
          >
            Try again
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
